import * as ko from 'knockout';
import LZString from 'lz-string';
import { ppFetch, ppFetchRaw } from '../shared/pp-fetch';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { NbaTeam } from './models/team';
import { NbaGame } from './models/game';
import { NbaWeek } from './models/week';
import { Series } from '../shared/models/series';
import { SelectionType } from '../shared/enums/selection-type';
import { RankMethod } from '../shared/enums/rank-method';
import { Conferences } from './enums';
import { Enumerable } from '../shared/linq';
import { userConfigManager } from '../shared/user-config-manager';
import { readConfig, getApimBaseUrl, getWebAppBaseUrl } from '../shared/config';
import { formatMonthOrdinalDay } from '../shared/date-helpers';
import type { IGame } from '../shared/interfaces/IGame';
import type { ITeam } from '../shared/interfaces/ITeam';
import type { ISeries } from '../shared/interfaces/ISeries';
import * as Information from './schedule-info';
import * as BracketGenerator from './bracket-generator';
import type { NbaTiebreakerTeam, GameResult } from '../shared/tiebreaker/types';
import { nbaConferenceRanker, nbaTwoWayTiebreaker, nbaMultiWayTiebreaker } from './tiebreaker/nba-tiebreaker';
import { ArrayGameResultsLookup } from '../shared/tiebreaker/game-results-lookup';
import { calcPct, calcPctNaN } from '../shared/tiebreaker/helpers';
import { toNbaTiebreakerTeam, applyTiebreakerResult, buildTeamObsMap } from '../shared/tiebreaker/adapters/ko-adapter';
import { mapDictionaryToArray, rankSort } from '../shared/helpers';
import { applyOfficialPlayoffOutcomes } from './official-playoff-overlay';
import { shouldApplyOfficialOverlay } from '../shared/should-apply-official-overlay';
import { getNbaDivisionWinnerState } from './division-winner-state';

// Re-export for entry point use
export { NbaApp };

// Expose static helpers on window for legacy Knockout template bindings
function exposeToWindow(app: NbaApp): void {
  (window as any).SchedulePicker = (window as any).SchedulePicker ?? {};
  (window as any).SchedulePicker.NBA = (window as any).SchedulePicker.NBA ?? {};
  (window as any).SchedulePicker.NBA.App = {
    mapDictionaryToArray: mapDictionaryToArray,
    RankSort: rankSort,
  };
  // Expose vm globally as the Knockout views reference vm.xxx
  (window as any).vm = app;
}

class NbaApp extends SchedulePickerBase {
  ConferenceTeams: ConferenceTeamDictionary;
  Teams: { key: string; value: Record<string, Record<string, KnockoutObservable<ITeam>>> }[];
  Dict: TeamDictionary;
  WeekDateMap: Record<number, any>;
  WesternTop: KnockoutObservable<KnockoutObservable<ITeam>[]>;
  EasternTop: KnockoutObservable<KnockoutObservable<ITeam>[]>;
  RNbaStyles: KnockoutObservable<boolean>;
  Warning: KnockoutObservable<string>;
  SkipDivisionRank: KnockoutObservable<boolean>;
  SeasonYearEnd = 2017;
  HtmlExport: KnockoutObservable<string>;
  Export: KnockoutObservable<string>;
  NumGames: KnockoutObservable<number>;
  ShowDivision: KnockoutObservable<boolean>;
  HideStatus: KnockoutObservable<boolean>;
  static postSeasonWeeks: number = 6;
  GameScheduleGameMap: GameScheduleMap = {};
  GameArray: number[];

  /**
   * Cached official Playoffs data, populated from the league response.
   * Re-applied via overlay on every UrlString change so both initial load
   * AND "Current Standings" reset hit the overlay. Defaults to an empty
   * Playoffs so the overlay short-circuits before ppFetch resolves.
   */
  cachedOfficialPlayoffs: ILeaguePlayoffs = { series: [] };

  Timeout: ReturnType<typeof setTimeout> | undefined;
  randomCallback: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const config = readConfig();
    super('basketball', config);
    const self = this;

    self.Hash = ko.observable(window.location.hash);
    self.GameArray = [];
    self.HideStatus = ko.observable(userConfigManager.UserConfig.NBA.HideStatus);
    self.HideStatus.subscribe((newVal: boolean) => {
      if (newVal !== userConfigManager.UserConfig.NBA.HideStatus) {
        userConfigManager.UserConfig.NBA.HideStatus = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.ShowDivision = ko.observable(userConfigManager.UserConfig.NBA.DivToggle);
    self.NumberOfTeams(30);
    self.Warning = ko.observable<string>('');
    self.HtmlExport = ko.observable('');
    self.Export = ko.observable('');
    self.EmptySeasonString = 'Aw18ZXTt-DFOS1b0c17Pd-wYUcSaWeRZVdTbXfQ408y5UA';
    self.NumGames = ko.observable(0);

    super.startComputing();
    self.SaveString.subscribe(() => {
      self.setExport();
    });

    const handleLeagueData = (
      leagueName: string,
      seasonYear: number,
      currentWeekString: string,
      isCustomLeague: boolean,
      westernTeams: ITeamData[],
      easternTeams: ITeamData[],
      games: IGameData[],
      replaceTeams: any[]
    ) => {
      setTimeout(() => {
        if ((window as any).PlayoffPredictor?.Spin?.hide) {
          (window as any).PlayoffPredictor.Spin.hide();
        }
      }, 2000);

      if (seasonYear) { self.SeasonYearEnd = seasonYear; }
      if (currentWeekString) { self.CurrentSeasonString(currentWeekString); }
      self.LeagueName(leagueName);
      self.IsCustomLeague(isCustomLeague);

      const dict: TeamDictionary = {};
      let teamObj: KnockoutObservable<ITeam>;
      self.ConferenceTeams = {} as ConferenceTeamDictionary;
      self.ConferenceTeams['Western'] = {} as Record<string, KnockoutObservable<ITeam>>;
      dict['Western'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
      ko.utils.arrayForEach(westernTeams, (team: ITeamData) => {
        dict['Western'][team.Division] = dict['Western'][team.Division] ?? {};
        teamObj = ko.observable(new NbaTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo));
        dict['Western'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['Western'][team.TeamAbbreviation] = teamObj;
      });
      dict['Eastern'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
      self.ConferenceTeams['Eastern'] = {} as Record<string, KnockoutObservable<ITeam>>;
      ko.utils.arrayForEach(easternTeams, (team: ITeamData) => {
        teamObj = ko.observable(new NbaTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo));
        dict['Eastern'][team.Division] = dict['Eastern'][team.Division] ?? {};
        dict['Eastern'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['Eastern'][team.TeamAbbreviation] = teamObj;
      });
      self.Dict = dict;
      self.Teams = mapDictionaryToArray(self.Dict);
      self.Weeks = Information.GetSchedule(self.Dict, games);
      self.MaxWeek(Enumerable.From(games).Max((g: IGameData) => g.Week));
      self.MaxWeek.valueHasMutated();
      self.MaxWeek.notifySubscribers();
      self.TotalWeeks = self.MaxWeek() + NbaApp.postSeasonWeeks;
      self.setGameScheduleNumbers(games.length + 113);

      self.WeekDateMap = {} as Record<number, any>;
      for (const weekIdx in self.Weeks) {
        self.WeekDateMap[weekIdx] = self.WeekDateMap[weekIdx] ?? {};
        const week = self.Weeks[weekIdx];
        let weekGames = week[0].games().concat(week[1].games());
        if (week.length === 3) { weekGames = weekGames.concat(week[2].games()); }
        const orderedGames = Enumerable.From(weekGames)
          .Where((p: KnockoutObservable<IGame>) => p != null)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder());
        if (orderedGames.Any()) {
          self.WeekDateMap[weekIdx].DayString = ko.computed(() =>
            `Week ${weekIdx}: ${formatMonthOrdinalDay(orderedGames.First()().gameDate())} - ${formatMonthOrdinalDay(orderedGames.Last()().gameDate())}`
          );
        } else {
          self.WeekDateMap[weekIdx].DayString = ko.computed(() => 'In-Season Tournament');
        }
        for (const gameObs of weekGames) {
          const game = gameObs();
          self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
        }
      }
      // Ensure post-season week entries exist before assigning DayString
      for (let i = 1; i <= NbaApp.postSeasonWeeks; i++) {
        self.WeekDateMap[self.MaxWeek() + i] = self.WeekDateMap[self.MaxWeek() + i] || {};
      }
      self.WeekDateMap[self.MaxWeek() + 1].DayString = ko.computed(() => 'Play-In RD 1');
      self.WeekDateMap[self.MaxWeek() + 2].DayString = ko.computed(() => 'Play-In RD 2');
      self.WeekDateMap[self.MaxWeek() + 3].DayString = ko.computed(() => 'RD 1');
      self.WeekDateMap[self.MaxWeek() + 4].DayString = ko.computed(() => 'Conference Semifinals');
      self.WeekDateMap[self.MaxWeek() + 5].DayString = ko.computed(() => 'Conference Finals');
      self.WeekDateMap[self.TotalWeeks].DayString = ko.computed(() => 'Finals');

      self.SelectedWeek({ key: 1, value: self.Weeks[1] });
      self.NumGames(games.length);
      self.Warning.subscribe((newVal) => { if (newVal) { (document.querySelector('.alert') as any)?.alert?.(); } });
      self.WesternTop = ko.observable([]);
      self.EasternTop = ko.observable([]);
      self.RNbaStyles = ko.observable<boolean>(false);
      self.RNbaStyles.subscribe(() => { self.setExport(); });
      self.SkipDivisionRank = ko.observable<boolean>(false);

      self.init(replaceTeams);

      if (window.location.hash) {
        try {
          const teamName = window.location.hash.slice(1);
          const searchTeam = self.findTeam(teamName);
          if (searchTeam) { self.SelectedTeam(searchTeam()); }
        } catch (ex) {
          console.warn(ex);
        }
      }

      self.UrlString.subscribe((newString) => {
        if (newString && newString.length) {
          newString = newString.replace(/%20/gi, '+');
          const decompress = LZString.decompressFromEncodedURIComponent(newString);
          if (decompress) {
            self.load(decompress);

            // Apply the cached official Playoffs ONLY when this load represents
            // the official current state — i.e., the loaded save string equals
            // CurrentSeasonString (the server-emitted current week save string).
            // User-modified loads from `?L=`, localStorage, or watchlists must
            // NOT be overwritten by IRL outcomes. The gate covers both the
            // initial ppFetch path (UrlString set from `?L=`/localStorage matches
            // CurrentSeasonString iff the user has not modified picks) and the
            // "Current Standings" reset path (setCurrentSeason → save-strings
            // fetch → UrlString set to CurrentSeasonString → here → match → overlay).
            if (shouldApplyOfficialOverlay(newString, self.CurrentSeasonString())) {
              applyOfficialPlayoffOutcomes(self, self.cachedOfficialPlayoffs);
            }

            // Auto-advance SelectedWeek to the latest playoff round with any pick.
            // Walks Finals → ConferenceFinal → ConferenceSemifinal → FirstRound →
            // PlayInRound2 → PlayInRound1. First round with a selectionMade game
            // wins; sets BOTH SelectedWeek AND BiggestWeek (tweak #19 —
            // setCurrentSeason's switchContext would otherwise clobber the change).
            //
            // NBA playoff games live inside slot.Series()[i].games(),
            // NOT in slot.GamesComputed() (tweak #13 — playoff weeks have empty
            // GamesComputed because games hang off Series, not the flat array).
            const maxWeek = self.MaxWeek();
            const numGames = self.NumGames();

            // Skip auto-advance when reg-season is fully empty (tweak #20). On a
            // fresh page with no picks, the picker generates a default bracket
            // from arbitrary tiebreaker output — overlay-applied playoff state
            // can land on coincidental team-pair overlaps and yank the user to
            // a playoff round instead of week 1.
            let regSeasonHasAny = false;
            for (let i = 0; i < numGames; i++) {
              if (self.GameArray[i]) {
                regSeasonHasAny = true;
                break;
              }
            }
            if (!regSeasonHasAny) {
              return;
            }

            for (let weekIdx = self.TotalWeeks; weekIdx > maxWeek; weekIdx--) {
              const weekSlots = self.Weeks[weekIdx];
              if (!weekSlots) {
                continue;
              }
              let hasSelection = false;
              for (const slot of weekSlots) {
                const series = (slot as NbaWeek).Series();
                for (const s of series) {
                  for (const gameObs of s.games()) {
                    if (gameObs().selectionMade()) {
                      hasSelection = true;
                      break;
                    }
                  }
                  if (hasSelection) {
                    break;
                  }
                }
                if (hasSelection) {
                  break;
                }
              }
              if (hasSelection) {
                self.SelectedWeek({ key: weekIdx, value: self.Weeks[weekIdx] });
                self.BiggestWeek(weekIdx);
                break;
              }
            }
          }
        } else {
          if ((window as any).PlayoffPredictor?.Spin?.hide) {
            (window as any).PlayoffPredictor.Spin.hide();
          }
        }
      });

      if (self.LoadedWatchlist()) {
        self.UrlString(self.LoadedWatchlist().SaveString());
      } else {
        self.UrlString(SchedulePickerBase.getQueryVariable('L') ?? localStorage.getItem(self.LeagueGuid) ?? '');
      }

      document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.style.display = 'none'; }
      setTimeout(() => {}, 80);

      ko.applyBindings(self, document.getElementById('content'));
      const navbars = document.querySelectorAll<HTMLElement>('.navbar');
      const nav0H = navbars[0]?.offsetHeight ?? 0;
      const nav1H = navbars[1]?.offsetHeight ?? 0;
      document.body.style.paddingTop = (nav0H + nav1H) + 'px';
      document.querySelectorAll('[data-toggle="popover"]').forEach(el => (el as any).popover?.());

      super.initTeamSubscribers();
      self.setExport();
      super.finishComputing();
    };

    // Get leagueGuid from data attribute (set from Razor: <div id="app-config" data-league-guid="...">)
    const appConfigEl = document.getElementById('app-config');
    const leagueGuid = appConfigEl?.dataset.leagueGuid ?? (window as any).leagueGuid ?? '';
    (window as any).leagueGuid = leagueGuid;
    const sportId = appConfigEl?.dataset.sportId;
    (self as any).LeagueGuid = leagueGuid;

    // All official leagues load from V2 API
    ppFetch<any>({
      url: `${getApimBaseUrl()}/LeagueEngine/leagues/${sportId}/${leagueGuid}/official`,
      method: 'GET',
    }).then((result: any) => {
      // Cache official playoff data BEFORE handleLeagueData triggers UrlString,
      // so the UrlString subscriber's overlay call sees the populated cache on
      // initial load. Subsequent reloads (Current Standings reset, etc.) reuse
      // the same cache.
      self.cachedOfficialPlayoffs = result.league.playoffs ?? { series: [] };

      const teamById: Record<number, ITeamDataV2> = {};
      result.teams.forEach((t: ITeamDataV2) => { teamById[t.teamId] = t; });
      const westernTeams: ITeamData[] = result.teams
        .filter((t: ITeamDataV2) => t.conference === 'Western')
        .map((t: ITeamDataV2) => ({
          TeamAbbreviation: t.teamAbbreviation, RegionName: t.regionName,
          TeamName: t.teamName, Conference: t.conference, Division: t.division, TeamID: t.teamId, TeamLogo: t.teamLogo,
        } as ITeamData));
      const easternTeams: ITeamData[] = result.teams
        .filter((t: ITeamDataV2) => t.conference === 'Eastern')
        .map((t: ITeamDataV2) => ({
          TeamAbbreviation: t.teamAbbreviation, RegionName: t.regionName,
          TeamName: t.teamName, Conference: t.conference, Division: t.division, TeamID: t.teamId, TeamLogo: t.teamLogo,
        } as ITeamData));
      const v1Games: IGameData[] = result.league.games.map((g: IGameDataV2) => {
        const home = teamById[g.homeId];
        const away = teamById[g.awayId];
        return {
          Home: { TeamAbbreviation: home.teamAbbreviation, RegionName: home.regionName, TeamName: home.teamName, Conference: home.conference, Division: home.division, TeamID: home.teamId, TeamLogo: home.teamLogo },
          Away: { TeamAbbreviation: away.teamAbbreviation, RegionName: away.regionName, TeamName: away.teamName, Conference: away.conference, Division: away.division, TeamID: away.teamId, TeamLogo: away.teamLogo },
          Week: g.week, GameTypeID: 0, GameDate: g.gameDate, GameKey: g.gameKey, GameGUID: g.gameGuid, GameScheduleNum: g.gameScheduleNum,
        } as IGameData;
      });
      handleLeagueData(result.league.leagueName, result.league.superbowlYear, result.league.currentWeekString, false, westernTeams, easternTeams, v1Games, []);
    }).catch((err: unknown) => {
      const e = err as Error;
      console.error('NBA schedule-picker load error:', e?.message, e?.stack, err);
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.textContent = `An error occurred: ${e?.message ?? String(err)}`; }
      if ((window as any).PlayoffPredictor?.Spin?.hide) {
        (window as any).PlayoffPredictor.Spin.hide();
      }
    });

    const bracketsEl = document.getElementById('brackets');
    if (bracketsEl) { bracketsEl.style.display = 'none'; }
    document.addEventListener('click', (e: MouseEvent) => {
      if ((e.target as Element).closest('#bracketUpload')) {
        const brackets = document.getElementById('brackets');
        if (!brackets) return;
        brackets.querySelectorAll('a').forEach(a => a.remove());
        const uploadingSpan = document.createElement('span');
        uploadingSpan.className = 'pull-right';
        uploadingSpan.textContent = 'Uploading...';
        brackets.appendChild(uploadingSpan);
        const canvas = document.querySelector<HTMLCanvasElement>('canvas');
        if (!canvas) return;
        const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
        const formData = new URLSearchParams();
        formData.set('base64', dataUrl);
        formData.set('leagueGuid', leagueGuid);
        formData.set('saveString', SchedulePickerBase.getQueryVariable('L') ?? localStorage.getItem(leagueGuid) ?? '');
        ppFetchRaw('/basketball/CustomLeague/UploadBracket', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString(),
        }).then(r => r.json()).then((ajaxResult: any) => {
          brackets.querySelectorAll('span').forEach(s => s.remove());
          const linkA = document.createElement('a');
          linkA.href = `${getWebAppBaseUrl()}/${ajaxResult.Model.link}`;
          linkA.className = 'pull-right';
          linkA.textContent = 'Bracket Link';
          brackets.appendChild(linkA);
          const tweetText = `I just picked who I think will win next year's #NBA Finals on %23PlayoffPredictors ${getWebAppBaseUrl()}${ajaxResult.Model.link}`;
          const tweetA = document.createElement('a');
          tweetA.className = 'twitter-share-button';
          tweetA.href = `https://x.com/intent/tweet?text=${tweetText}`;
          tweetA.textContent = 'Tweet';
          brackets.appendChild(tweetA);
          if ((window as any).twttr) {
            (window as any).twttr.widgets.load(brackets);
          }
        });
      }
    });

    // Expose to window for Knockout template bindings
    exposeToWindow(self);
  }

  get LeagueGuid(): string {
    return (this as any)._leagueGuid ?? '';
  }

  set LeagueGuid(value: string) {
    (this as any)._leagueGuid = value;
  }

  setExport(): void {
    const self = this;
    const html = (() => {
      let western = '<h3>Western</h3><div>';
      for (const div of mapDictionaryToArray(self.Teams[0].value)) {
        western += `<table><thead><tr><th>#</th><th>${div.key}</th><th>WL</th><th>Div</th></tr></thead><tbody>`;
        for (const teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          const team = (teamRef as any).value() as NbaTeam;
          const style = team.ConferenceCss() === 'in-post-season' ? 'color:green; font-weight:bold' : 'color:red;font-style:italic;';
          western += `<tr><td style="${style}">${team.ConferenceRankText()}</td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamName}</a></td><td>${team.GameRecords.AllRecord.toString()}</td><td>${team.GameRecords.DivisionRecord.toString()}</td></tr>`;
        }
        western += '</table>';
      }
      western += '</div>';

      let eastern = '<div><h3>Eastern</h3>';
      for (const div of mapDictionaryToArray(self.Teams[1].value)) {
        eastern += `<table><thead><tr><th>#</th><th>${div.key}</th><th>WL</th><th>Div</th></tr></thead><tbody>`;
        for (const teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          const team = (teamRef as any).value() as NbaTeam;
          const style = team.ConferenceCss() === 'in-post-season' ? 'color:green; font-weight:bold' : 'color:red;font-style:italic;';
          eastern += `<tr><td style="${style}">${team.ConferenceRankText()}</td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamName}</a></td><td>${team.GameRecords.AllRecord.toString()}</td><td>${team.GameRecords.DivisionRecord.toString()}</td></tr>`;
        }
        eastern += '</table>';
      }
      eastern += '</div>';

      return `<div>${western}${eastern}<a href='${self.SaveVal()}'>Generated By The NBA Playoff Predictor</a></div>`;
    })();
    self.HtmlExport(html);

    const redditExport = (() => {
      const exportArray: any[] = [];
      let conferenceName = 'Eastern';
      if (self.RNbaStyles()) {
        conferenceName = '[](/Eastern)Eastern';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(self.EasternTop(), (item: KnockoutObservable<NbaTeam>, idx: number) => {
        let teamName = '[' + item().TeamAbbreviation + '](' + self.SaveVal() + '#' + item().TeamAbbreviation + ')';
        if (self.RNbaStyles()) {
          teamName = '[](/' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push((idx + 1), '|', teamName, '|', item().GameRecords.AllRecord.Wins(), '|', item().GameRecords.AllRecord.Losses(), '\n');
      });
      exportArray.push('\n\n');
      conferenceName = 'Western';
      if (self.RNbaStyles()) {
        conferenceName = '[](/Western)Western';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(self.WesternTop(), (item: KnockoutObservable<NbaTeam>, idx: number) => {
        let teamName = '[' + item().TeamAbbreviation + '](' + self.SaveVal() + '#' + item().TeamAbbreviation + ')';
        if (self.RNbaStyles()) {
          teamName = '[](/' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push((idx + 1), '|', teamName, '|', item().GameRecords.AllRecord.Wins(), '|', item().GameRecords.AllRecord.Losses(), '\n');
      });
      exportArray.push('\n\nGenerated by The [NBA Playoff Predictor](', self.SaveVal(), ')');
      return exportArray.join('');
    })();
    self.Export(redditExport);
  }

  previewHtml(): void {
    const win = window.open(
      '',
      'Title',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=780, height=200, top=${screen.height - 400}, left=${screen.width - 840}`
    );
    if (win) {
      win.document.body.innerHTML = this.HtmlExport();
    }
  }

  init(replaceTeams?: any[]): void {
    const self = this;
    const allTeams: KnockoutObservable<ITeam>[] = [];
    const WesternDivisions = this.Dict['Western'];
    for (const divisionInd in WesternDivisions) {
      const division = WesternDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }
    const EasternDivisions = this.Dict['Eastern'];
    for (const divisionInd in EasternDivisions) {
      const division = EasternDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }
    this.AllTeams(allTeams);

    if (replaceTeams) {
      for (const teamOverride of replaceTeams) {
        const teamReplaced: any = Enumerable.From(this.AllTeams()).First((p: any) => p().TeamID === teamOverride.TeamReplaced);
        this.AllTeams.remove(teamReplaced);
        teamReplaced().RegionName = teamOverride.RegionName;
        teamReplaced().TeamName = teamOverride.TeamName;
        teamReplaced().Conference = teamOverride.Conference;
        teamReplaced().Division = teamOverride.Division;
        this.AllTeams.push(teamReplaced);
      }
    }

    this.SelectedTeam.subscribe((newVal) => {
      if (newVal) {
        this.IsTeamSelected(true);
        setTimeout(() => {
          const adbox = document.getElementById('team-adbox');
          const statstable = document.getElementById('team-statstable');
          if (adbox && statstable) {
            adbox.style.width = statstable.offsetWidth + 'px';
            adbox.style.height = (statstable.offsetHeight * 3) + 'px';
            const ins = adbox.querySelector<HTMLElement>('ins');
            if (ins) { ins.style.height = (adbox.offsetHeight * 2) + 'px'; }
          }
        }, 1000);
        newVal.Schedule(this.getScheduleForTeam(newVal));
        (newVal as NbaTeam).FakeObs.valueHasMutated();
      }
    });
  }

  unselectGame(gameInput: KnockoutObservable<IGame>, skipUpdate?: boolean): void {
    const self = this;
    gameInput().selectionMade(false);
    gameInput().teamSelected('NONE');
    self.GameArray[gameInput().gameScheduleNum()] = 0;
    self.updateSaveString();
    if (!skipUpdate) {
      self.UpdateStats(-1, true);
    }
  }

  load(urlString: string): void {
    const self = this;
    const numGames = self.NumGames();
    self.BlockHistoryPush(true);
    let biggestWeek = 1;

    if (self.IsAfterFirstLoad) {
      for (const weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
          ko.utils.arrayForEach(week.Series(), (series: ISeries) => {
            ko.utils.arrayForEach(series.games(), (game: KnockoutObservable<IGame>) => {
              game().selectionType(SelectionType.NONE);
              game().selectionMade(false);
              game().teamSelected('');
            });
          });
        });
      }
      const bracketsDiv = document.getElementById('brackets');
      if (bracketsDiv) { bracketsDiv.innerHTML = ''; }
      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.reset();
        team.FakeObs();
      }
      self.GameArray.fill(SelectionType.NONE, urlString.length);
    }

    for (let i = 0; i < urlString.length; i++) {
      const game = self.GameScheduleGameMap[i];
      if (!game) continue;
      const _game = game();
      const home = _game.home() as NbaTeam;
      const away = _game.away() as NbaTeam;
      const weekInd = _game.week;

      const input = {
        gameGuid: _game.gameGuid,
        home: {
          Conference: home.Conference,
          Division: home.Division,
          TeamName: home.TeamName,
          Pct: home.GameRecords.AllRecord.Pct,
        },
        away: {
          Conference: away.Conference,
          Division: away.Division,
          TeamName: away.TeamName,
          Pct: away.GameRecords.AllRecord.Pct,
        },
      } as unknown as IGame;

      const skipUpdate = i < numGames;
      let selection = parseInt(urlString[i], 10);
      if (!selection) { selection = 0; }

      switch (selection) {
        case SelectionType.AWAY_WIN:
          self.GameArray[i] = SelectionType.AWAY_WIN;
          if (biggestWeek < +weekInd) { biggestWeek = +weekInd; }
          if (i < numGames) {
            (away as any).Wins.push(input);
            (home as any).Losses.push(input);
            self.gameSelected(game, game().away, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, game().away, selection, true);
          }
          break;
        case SelectionType.HOME_WIN:
          self.GameArray[i] = SelectionType.HOME_WIN;
          if (biggestWeek < +weekInd) { biggestWeek = +weekInd; }
          if (i < numGames) {
            (away as any).Losses.push(input);
            (home as any).Wins.push(input);
            self.gameSelected(game, game().home, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, game().home, selection, true);
          }
          break;
        case SelectionType.TIE:
          break;
        case SelectionType.NONE:
          self.GameArray[i] = SelectionType.NONE;
          if (i < numGames) {
            self.unselectGame(game, skipUpdate);
          } else {
            self.selectSeriesGame(game, null, 0, true);
          }
          break;
      }

      if (i === numGames - 1) {
        super.initTeamSubscribers();
        this.UpdateStats(i, true);
      }

      // play-in round 2
      if (i === (self.Weeks[self.MaxWeek() + 1][2].Series()[1]?.games()[0]()?.gameScheduleNum() ?? -1)) {
        self.seedPlayIn2();
      }
      // playoffs rd 1
      if (i === (self.Weeks[self.MaxWeek() + 2][2].Series()[0]?.games()[0]()?.gameScheduleNum() ?? -1)) {
        self.seedRd1();
      }
      // conf semi
      if (i === (self.Weeks[self.MaxWeek() + 3][0].Series()[0]?.games()[0]()?.gameScheduleNum() ?? -1)) {
        self.seedConfSemiFinals();
      }
      // CG
      if (i === (self.Weeks[self.MaxWeek() + 4][0].Series()[0]?.games()[0]()?.gameScheduleNum() ?? -1)) {
        self.seedCG();
      }
      // Finals
      if (i === (self.Weeks[self.MaxWeek() + 5][0].Series()[2]?.games()[6]()?.gameScheduleNum() ?? -1)) {
        self.seedFinals();
      }
    }

    self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
    self.BiggestWeek(biggestWeek);

    if (self.Hash()) {
      try {
        const team = self.findTeam(self.Hash().slice(1))();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }

    self.IsAfterFirstLoad = true;
    self.BlockHistoryPush(true);
    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  seedPlayIn2(): void {
    const self = this;
    const rd1Week = self.MaxWeek() + 1;
    const rd2Week = rd1Week + 1;
    const week2WCRef = this.Weeks[rd2Week][0] as NbaWeek;
    const week2ECRef = this.Weeks[rd2Week][2] as NbaWeek;

    week2WCRef.Series([]);
    const rd1WCWinners = this.Weeks[rd1Week][0].Series();
    if (rd1WCWinners.length === 2 && rd1WCWinners[0].seriesDecided() && rd1WCWinners[1].seriesDecided()) {
      week2WCRef.Series([]);
      this.generateSeries(week2WCRef, rd1WCWinners[0].seriesLoser(), rd1WCWinners[1].seriesWinner(), rd2Week, 1, self.NumGames() + 4 - 1);
      week2WCRef.Series()[0].seriesWinner.subscribe((winner) => {
        if (!winner) return;
        if (week2WCRef.Series()[0].seriesDecided()) {
          self.SwapSeriesSeeds(week2WCRef.Series()[0], [8, 9, 10]);
        }
      });
    }

    week2ECRef.Series([]);
    const rd1ECWinners = this.Weeks[rd1Week][2].Series();
    if (rd1ECWinners.length === 2 && rd1ECWinners[0].seriesDecided() && rd1ECWinners[1].seriesDecided()) {
      week2ECRef.Series([]);
      this.generateSeries(week2ECRef, rd1ECWinners[0].seriesLoser(), rd1ECWinners[1].seriesWinner(), rd2Week, 1, self.NumGames() + 5 - 1);
      week2ECRef.Series()[0].seriesWinner.subscribe((winner) => {
        if (!winner) return;
        if (week2ECRef.Series()[0].seriesDecided()) {
          self.SwapSeriesSeeds(week2ECRef.Series()[0], [8, 9, 10]);
        }
      });
    }

    for (let i = rd2Week; i <= this.TotalWeeks; i++) {
      for (let j = 0; j < 3; j++) {
        for (let k = 0; k < this.Weeks[i][j].Series().length - 1; k++) {
          this.clearSeries(this.Weeks[i][j].Series()[k]);
        }
      }
    }
  }

  seedRd1(): void {
    const self = this;
    self.WesternTop(self.WesternTop().sort((a, b) => a().ConferenceRank() - b().ConferenceRank()));
    self.EasternTop(self.EasternTop().sort((a, b) => a().ConferenceRank() - b().ConferenceRank()));

    const playInRd2Week = self.MaxWeek() + 2;
    const rd1Week = playInRd2Week + 1;
    let gameNum = self.NumGames() + 5;

    if (
      self.Weeks[playInRd2Week][0].Series().length === 1 &&
      self.Weeks[playInRd2Week][0].Series()[0].seriesDecided() &&
      self.Weeks[playInRd2Week - 1][0].Series().every((s) => s && s.seriesDecided())
    ) {
      self.Weeks[rd1Week][0].Series([]);
      gameNum = this.generateSeries(self.Weeks[rd1Week][0], self.WesternTop()[7], self.WesternTop()[0], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][0], self.WesternTop()[4], self.WesternTop()[3], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][0], self.WesternTop()[5], self.WesternTop()[2], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][0], self.WesternTop()[6], self.WesternTop()[1], rd1Week, 7, gameNum);
    }

    if (
      self.Weeks[playInRd2Week][2].Series().length === 1 &&
      self.Weeks[playInRd2Week][2].Series()[0].seriesDecided() &&
      self.Weeks[playInRd2Week - 1][2].Series().every((s) => s.seriesDecided())
    ) {
      self.Weeks[rd1Week][2].Series([]);
      gameNum = this.generateSeries(self.Weeks[rd1Week][2], self.EasternTop()[7], self.EasternTop()[0], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][2], self.EasternTop()[4], self.EasternTop()[3], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][2], self.EasternTop()[5], self.EasternTop()[2], rd1Week, 7, gameNum);
      gameNum = this.generateSeries(self.Weeks[rd1Week][2], self.EasternTop()[6], self.EasternTop()[1], rd1Week, 7, gameNum);
    }

    for (let i = rd1Week; i < this.TotalWeeks; i++) {
      for (let j = 0; j < 3; j++) {
        for (let k = 0; k < this.Weeks[i][j].Series().length - 1; k++) {
          this.clearSeries(this.Weeks[i][j].Series()[k]);
        }
      }
    }
  }

  seedConfSemiFinals(): void {
    const self = this;
    const rd1Week = self.MaxWeek() + 3;
    const semifinals = self.MaxWeek() + 4;
    const westernSeriesRecord = this.Weeks[rd1Week][0].Series();
    const easternSeriesRecord = this.Weeks[rd1Week][2].Series();
    let gameNum = self.NumGames() + 61;

    if (
      westernSeriesRecord.length === 4 &&
      westernSeriesRecord[0].seriesDecided() &&
      westernSeriesRecord[1].seriesDecided() &&
      westernSeriesRecord[2].seriesDecided() &&
      westernSeriesRecord[3].seriesDecided()
    ) {
      this.Weeks[semifinals][0].Series([]);
      gameNum = this.generateSeries(this.Weeks[semifinals][0], westernSeriesRecord[1].seriesWinner(), westernSeriesRecord[0].seriesWinner(), semifinals, 7, gameNum);
      gameNum = this.generateSeries(this.Weeks[semifinals][0], westernSeriesRecord[3].seriesWinner(), westernSeriesRecord[2].seriesWinner(), semifinals, 7, gameNum);
      for (const series of this.Weeks[semifinals][0].Series()) {
        this.clearSeries(series);
      }
    }

    if (
      easternSeriesRecord.length === 4 &&
      easternSeriesRecord[0].seriesDecided() &&
      easternSeriesRecord[1].seriesDecided() &&
      easternSeriesRecord[2].seriesDecided() &&
      easternSeriesRecord[3].seriesDecided()
    ) {
      this.Weeks[semifinals][2].Series([]);
      gameNum = this.generateSeries(this.Weeks[semifinals][2], easternSeriesRecord[1].seriesWinner(), easternSeriesRecord[0].seriesWinner(), semifinals, 7, gameNum);
      gameNum = this.generateSeries(this.Weeks[semifinals][2], easternSeriesRecord[3].seriesWinner(), easternSeriesRecord[2].seriesWinner(), semifinals, 7, gameNum);
      for (const series of this.Weeks[semifinals][2].Series()) {
        this.clearSeries(series);
      }
    }
  }

  seedCG(): void {
    const self = this;
    const dvWeek = self.MaxWeek() + 4;
    const cgWeek = self.MaxWeek() + 5;
    const westernSeriesRecord = this.Weeks[dvWeek][0].Series();
    const easternSeriesRecord = this.Weeks[dvWeek][2].Series();
    let gameNum = self.NumGames() + 89;

    if (westernSeriesRecord.length === 2 && westernSeriesRecord[0].seriesDecided() && westernSeriesRecord[1].seriesDecided()) {
      const dvTeams = Enumerable.From(westernSeriesRecord)
        .OrderBy((series: ISeries) => series.seriesWinner()().ConferenceRank())
        .Select((series: ISeries) => series.seriesWinner())
        .ToArray();
      this.Weeks[cgWeek][0].Series([]);
      gameNum = this.generateSeries(this.Weeks[cgWeek][0], dvTeams[0], dvTeams[1], cgWeek, 7, gameNum);
      this.clearSeries(this.Weeks[cgWeek][0].Series()[0]);
    }

    if (easternSeriesRecord.length === 2 && easternSeriesRecord[0].seriesDecided() && easternSeriesRecord[1].seriesDecided()) {
      const dvTeams = Enumerable.From(easternSeriesRecord)
        .OrderBy((series: ISeries) => series.seriesWinner()().ConferenceRank())
        .Select((series: ISeries) => series.seriesWinner())
        .ToArray();
      this.Weeks[cgWeek][2].Series([]);
      this.generateSeries(this.Weeks[cgWeek][2], dvTeams[0], dvTeams[1], cgWeek, 7, gameNum);
      this.clearSeries(this.Weeks[cgWeek][2].Series()[0]);
    }
  }

  seedFinals(): void {
    const self = this;
    const cgWeek = self.MaxWeek() + 5;
    const cfWeek = self.TotalWeeks;
    const westernSeriesRecord = this.Weeks[cgWeek][0].Series();
    const easternSeriesRecord = this.Weeks[cgWeek][2].Series();

    if (westernSeriesRecord.length && easternSeriesRecord.length && westernSeriesRecord[0].seriesDecided() && easternSeriesRecord[0].seriesDecided()) {
      // Derive the Finals' first gameScheduleNum from the Eastern conference-finals
      // series (game 7). This MUST stay inside the guard: a save string can encode
      // a Finals-week selection while seedCG() never generated the conference-finals
      // series (e.g. the loaded state leaves a conference's semifinals not both
      // decided). Reading easternSeriesRecord[0] before the guard then threw
      // "Cannot read properties of undefined (reading 'games')" and the picker
      // failed to load.
      const gameNumber = easternSeriesRecord[0].games()[6]().gameScheduleNum();
      const dvTeams = this.getHomeCourtAdvantageForFinals([westernSeriesRecord[0].seriesWinner(), easternSeriesRecord[0].seriesWinner()]);
      this.Weeks[cfWeek][1].Series([]);
      this.generateSeries(this.Weeks[cfWeek][1], dvTeams[0], dvTeams[1], cfWeek, 7, gameNumber, true);
      this.clearSeries(this.Weeks[cfWeek][1].Series()[0]);
    }
  }

  randomize(): void {
    const self = this;
    if (confirm('All of your regular season selections will be randomized, replacing your current selections. Are you sure you want to proceed?')) {
      for (let i = 0; i < 1230; i++) {
        const selection = (Math.floor(Math.random() * 2) + 1) as SelectionType;
        self.GameArray[i] = selection;
      }
      for (let i = 1230; i < 1340; i++) {
        self.GameArray[i] = 0;
      }
      self.updateSaveString();
      self.load(self.GameArray.join(''));
      for (const team of self.AllTeams()) {
        (team() as NbaTeam).FakeObs.valueHasMutated();
        team().Wins.valueHasMutated();
        team().Losses.valueHasMutated();
      }
      self.UpdateStats(-1, true);
      self.SelectedWeek({ key: 26, value: self.Weeks[26] });
    }
  }

  randomizeWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of week.games()) {
        const game = (gameObs as KnockoutObservable<NbaGame>)() as NbaGame;
        self.unselectGame(gameObs as KnockoutObservable<IGame>, true);
        let selection: SelectionType = SelectionType.NONE;
        if (self.SelectedWeek().key <= self.MaxWeek()) {
          selection = (Math.floor(Math.random() * 2) + 1) as SelectionType;
        }
        let teamSelected: any;
        switch (selection) {
          case SelectionType.AWAY_WIN:
            teamSelected = game.away;
            break;
          case SelectionType.HOME_WIN:
            teamSelected = game.home;
            break;
          default:
            teamSelected = game.home;
            break;
        }
        self.selectGame(gameObs as KnockoutObservable<IGame>, teamSelected, selection, true);
        if (game.TeamThatWon()) {
          game.TeamThatWon()().GameRecords.valueHasMutated();
        }
        if (game.TeamThatLost()) {
          game.TeamThatLost()().GameRecords.valueHasMutated();
        }
      }
      for (const series of week.Series()) {
        series.reset();
      }
      for (const series of week.Series()) {
        for (const gameObs of series.games()) {
          if (series.seriesDecided()) break;
          const game = (gameObs as KnockoutObservable<NbaGame>)() as NbaGame;
          self.unselectGame(gameObs as KnockoutObservable<IGame>, true);
          const selection = (Math.floor(Math.random() * 2) + 1) as SelectionType;
          let teamSelected: any;
          switch (selection) {
            case SelectionType.AWAY_WIN: teamSelected = game.away; break;
            case SelectionType.HOME_WIN: teamSelected = game.home; break;
            default: teamSelected = game.home; break;
          }
          self.selectSeriesGame(gameObs as KnockoutObservable<IGame>, teamSelected, selection, true);
        }
        series.games.valueHasMutated();
        series.seriesDecided();
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  resetWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of week.games()) {
        const game = (gameObs as KnockoutObservable<IGame>)();
        if (game.selectionMade()) {
          if (game.selectionType() !== SelectionType.TIE) {
            self.selectGame(gameObs as KnockoutObservable<IGame>, game.TeamThatWon(), SelectionType.NONE, true);
          } else {
            self.selectGame(gameObs as KnockoutObservable<IGame>, 'TIE', SelectionType.NONE, true);
          }
        }
      }
      for (const series of week.Series()) {
        for (const gameObs of series.games()) {
          self.unselectGame(gameObs as KnockoutObservable<IGame>, true);
        }
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  /**
   * Returns all currently-generated playoff series across all 6 postseason
   * weeks (PlayIn R1 → R2 → First Round → Conf Semis → Conf Finals → NBA Finals)
   * and all 3 conference slots per week. Used by the official-playoff-overlay
   * to find series by team pair.
   */
  getAllPlayoffSeries(): Series[] {
    const self = this;
    const maxWeek = self.MaxWeek();
    const result: Series[] = [];

    for (let roundOffset = 1; roundOffset <= NbaApp.postSeasonWeeks; roundOffset++) {
      const weekSlots = self.Weeks[maxWeek + roundOffset];
      if (!weekSlots) {
        continue;
      }
      for (const slot of weekSlots) {
        for (const series of (slot as NbaWeek).Series()) {
          result.push(series);
        }
      }
    }
    return result;
  }

  selectGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize: boolean = false
  ): void {
    const self = this;
    const _gameInput = gameInput();
    const home = _gameInput.home() as NbaTeam;
    const away = _gameInput.away() as NbaTeam;
    const input = {
      gameGuid: _gameInput.gameGuid,
      home: {
        Conference: home.Conference,
        Division: home.Division,
        TeamName: home.TeamName,
        Pct: home.GameRecords.AllRecord.Pct,
      },
      away: {
        Conference: away.Conference,
        Division: away.Division,
        TeamName: away.TeamName,
        Pct: away.GameRecords.AllRecord.Pct,
      },
    } as unknown as IGame;

    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;

    if (typeof selectedTeam === 'function' && selectedTeam().TeamAbbreviation === _gameInput.teamSelected()) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = 0;
      (home as any).Wins.remove((p: IGame) => p.gameGuid === input.gameGuid);
      (away as any).Wins.remove((p: IGame) => p.gameGuid === input.gameGuid);
      (home as any).Losses.remove((p: IGame) => p.gameGuid === input.gameGuid);
      (away as any).Losses.remove((p: IGame) => p.gameGuid === input.gameGuid);
    } else {
      _gameInput.selectionMade(true);
      _gameInput.selectionType(selectionType);

      switch (selectionType) {
        case SelectionType.AWAY_WIN:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('NONE');
          break;
      }

      if (_gameInput.week <= self.MaxWeek()) {
        switch (selectionType) {
          case SelectionType.AWAY_WIN:
            _gameInput.teamSelected(away.TeamAbbreviation);
            (away as any).Wins.push(input);
            (home as any).Losses.push(input);
            break;
          case SelectionType.HOME_WIN:
            _gameInput.teamSelected(home.TeamAbbreviation);
            (home as any).Wins.push(input);
            (away as any).Losses.push(input);
            break;
          case SelectionType.NONE:
            _gameInput.teamSelected('NONE');
            break;
        }
      }
    }

    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      if (gameInput().week <= self.MaxWeek()) {
        if (home.Conference === away.Conference) {
          self.UpdateRankings(home.Conference, true, -1);
        } else {
          self.UpdateRankings(Conferences.Western, true, -1);
          self.UpdateRankings(Conferences.Eastern, true, -1);
        }
      }
      super.finishComputing();
      self.updateSaveString();
      self.pushHistory();
    }, 400);
  }

  selectSeriesGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: KnockoutObservable<ITeam> | null,
    selectionType: SelectionType,
    isRandomize: boolean,
    forceSet: boolean = false
  ): void {
    const self = this;
    const _gameInput = gameInput();
    const home = _gameInput.home() as NbaTeam;
    const away = _gameInput.away() as NbaTeam;
    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;
    const maxWeek = self.MaxWeek();
    const gameWeek = gameInput().week;

    if (
      !forceSet &&
      typeof selectedTeam === 'function' &&
      selectedTeam().TeamAbbreviation === _gameInput.teamSelected()
    ) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = SelectionType.NONE;
    } else {
      _gameInput.selectionMade(selectionType !== SelectionType.NONE);
      _gameInput.selectionType(selectionType);
      switch (selectionType) {
        case SelectionType.AWAY_WIN:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('NONE');
          break;
      }
    }

    if (gameWeek === maxWeek + 1) { self.seedPlayIn2(); }
    if (gameWeek === maxWeek + 2) { self.seedRd1(); }
    if (gameWeek === maxWeek + 3) { self.seedConfSemiFinals(); }
    if (gameWeek === maxWeek + 4) { self.seedCG(); }
    if (gameWeek === maxWeek + 5) { self.seedFinals(); }

    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      this.UpdateStatsPostSeason();
      super.finishComputing();
    }, 250);
  }

  clearSeries(series: ISeries): void {
    const self = this;
    for (const game of series.games()) {
      const _gameInput = (game as KnockoutObservable<IGame>)();
      const gameNum = _gameInput.gameScheduleNum();
      if (gameNum || gameNum === 0) {
        self.GameArray[gameNum] = SelectionType.NONE;
      }
    }
    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      self.updateSaveString();
      self.pushHistory();
      super.finishComputing();
    }, 250);
  }

  gameSelected(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: KnockoutObservable<ITeam> | string,
    selectionType: SelectionType,
    positionCounter?: number,
    skipUpdate?: boolean
  ): void {
    const self = this;
    gameInput().teamSelected(
      typeof selectedTeam === 'string' ? selectedTeam : selectedTeam().TeamAbbreviation
    );
    gameInput().selectionMade(true);
    gameInput().selectionType(selectionType);
    if (!skipUpdate) {
      self.UpdateStats(positionCounter, positionCounter === self.NumGames() - 1);
    }
  }

  SwapSeriesSeeds(seriesToSwap: ISeries, expectedSeeds: number[]): void {
    const winner = seriesToSwap.seriesWinner();
    const loser = seriesToSwap.seriesLoser();
    if (!(expectedSeeds.includes(winner().ConferenceRank()) && expectedSeeds.includes(loser().ConferenceRank()))) {
      this.UpdateStats(-1, true);
    }
    if (winner().ConferenceRank() > loser().ConferenceRank()) {
      const winnerRank = winner().ConferenceRank();
      const loserRank = loser().ConferenceRank();
      loser().ConferenceRank(winnerRank);
      winner().ConferenceRank(loserRank);
    }
    loser().ConferenceRank.valueHasMutated();
    winner().ConferenceRank.valueHasMutated();
  }

  UpdateRankings(conferenceName: string, fillWildCard: boolean, Position: number): void {
    const self = this;
    const maxWeek = self.MaxWeek();
    const westernWeekRef = this.Weeks[maxWeek + 1][0] as NbaWeek;
    const easternWeekRef = this.Weeks[maxWeek + 1][2] as NbaWeek;
    const teams: KnockoutObservable<NbaTeam>[] = [];
    const teamsInConference = this.Dict[conferenceName];

    if (fillWildCard || Position === -1) {
      for (const _conference in teamsInConference) {
        const conference = teamsInConference[_conference];
        for (const _team in conference) {
          teams.push(conference[_team] as KnockoutObservable<NbaTeam>);
        }
      }
      for (const team of teams) {
        team().Tiebreakers({ HTH: [], Sweeps: [], DivHTH: [], Explanations: [] });
      }

      const divisions = Enumerable.From(teams).GroupBy((p: any) => p().Division).ToArray();
      for (const division of divisions) {
        for (const divTeam of division.source) {
          (divTeam as KnockoutObservable<NbaTeam>)().RankMethod(null);
          (divTeam as KnockoutObservable<NbaTeam>)().IsDivisionWinner(false);
        }
        const winner = this.DivisionPickTop(division);
        for (const idx in division.source) {
          if (division.source[idx] === winner) continue;
          (division.source[idx] as KnockoutObservable<NbaTeam>)().Tiebreakers({ HTH: [], Sweeps: [], DivHTH: [], Explanations: [] });
        }
      }

      let teamsSorted = this.ConferenceRanker(teams as any);
      teamsSorted = Enumerable.From(teamsSorted).Where((p: any) => p != null).ToArray();
      ko.utils.arrayForEach(teamsSorted, (team: KnockoutObservable<NbaTeam>, index: number) => {
        team().ConferenceRank(index + 1);
        team().Tiebreakers.valueHasMutated();
      });

      if (conferenceName === Conferences.Western) {
        this.WesternTop(teamsSorted.slice(0, 10));
        const topWestern = this.WesternTop()[0]();
        const topWins = topWestern.GameRecords.AllRecord.Wins();
        const topLosses = topWestern.GameRecords.AllRecord.Losses();
        for (const team of teamsSorted) {
          const unwrapped = team() as NbaTeam;
          if (unwrapped === topWestern) {
            unwrapped.GamesBack(null);
          } else {
            unwrapped.GamesBack(this.CalcGamesBack(topWins, topLosses, unwrapped.GameRecords.AllRecord.Wins(), unwrapped.GameRecords.AllRecord.Losses()));
          }
        }
        if (fillWildCard) {
          westernWeekRef.Series([]);
          this.generateSeries(westernWeekRef, this.WesternTop()[6], this.WesternTop()[7], maxWeek + 1, 1, self.NumGames() - 1);
          westernWeekRef.Series()[0].seriesWinner.subscribe((winner) => {
            if (!winner) return;
            if (westernWeekRef.Series()[0].seriesDecided()) {
              self.SwapSeriesSeeds(westernWeekRef.Series()[0], [7, 8]);
            }
          });
          this.generateSeries(westernWeekRef, this.WesternTop()[8], this.WesternTop()[9], maxWeek + 1, 1, self.NumGames() + 1 - 1);
        }
      } else {
        this.EasternTop(teamsSorted.slice(0, 10));
        const topEastern = this.EasternTop()[0]();
        const topWins = topEastern.GameRecords.AllRecord.Wins();
        const topLosses = topEastern.GameRecords.AllRecord.Losses();
        for (const team of teamsSorted) {
          const unwrapped = team() as NbaTeam;
          if (unwrapped === topEastern) {
            unwrapped.GamesBack(0);
          } else {
            unwrapped.GamesBack(this.CalcGamesBack(topWins, topLosses, unwrapped.GameRecords.AllRecord.Wins(), unwrapped.GameRecords.AllRecord.Losses()));
          }
        }
        if (fillWildCard) {
          easternWeekRef.Series([]);
          this.generateSeries(easternWeekRef, this.EasternTop()[6], this.EasternTop()[7], maxWeek + 1, 1, self.NumGames() + 2 - 1);
          easternWeekRef.Series()[0].seriesWinner.subscribe((winner) => {
            if (!winner) return;
            if (easternWeekRef.Series()[0].seriesDecided()) {
              self.SwapSeriesSeeds(easternWeekRef.Series()[0], [7, 8]);
            }
          });
          this.generateSeries(easternWeekRef, this.EasternTop()[8], this.EasternTop()[9], maxWeek + 1, 1, self.NumGames() + 3 - 1);
          this.clearSeries(easternWeekRef.Series()[0]);
          this.clearSeries(easternWeekRef.Series()[1]);
        }
      }
    }

    if (fillWildCard) {
      for (let i = this.MaxWeek() + 1; i < this.TotalWeeks; i++) {
        for (let j = 0; j < 3; j++) {
          for (let k = 0; k < this.Weeks[i][j].Series().length - 1; k++) {
            this.clearSeries(this.Weeks[i][j].Series()[k]);
          }
        }
      }
    }
  }

  getHomeCourtAdvantageForFinals(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const team1 = teams[0];
    const team2 = teams[1];
    if (team1().GameRecords.AllRecord.Wins() === team2().GameRecords.AllRecord.Wins()) {
      // Save + restore per-team rank/explanation — this probe-only tiebreaker
      // must not mutate the standings that already exist on the teams.
      const rank1 = team1().RankMethod();
      const rank2 = team2().RankMethod();
      const explanation1 = JSON.stringify(team1().Tiebreakers());
      const explanation2 = JSON.stringify(team2().Tiebreakers());

      // Delegate to the extracted NBA two-way tiebreaker service, then fall back
      // to non-conference wins differential if the service couldn't separate
      // them via H2H (i.e. winner was chosen by downstream rules).
      const plainTeams = [team1, team2].map(o => toNbaTiebreakerTeam(
        o,
        getNbaDivisionWinnerState(o()),
      ));
      const games = this.buildGamesLookup([team1, team2]);
      const result = nbaTwoWayTiebreaker(plainTeams, games);

      team1().RankMethod(rank1);
      team2().RankMethod(rank2);
      team1().Tiebreakers(JSON.parse(explanation1));
      team2().Tiebreakers(JSON.parse(explanation2));

      const serviceRank = result.rankMethods.get(result.orderedTeams[0].teamAbbreviation);
      const separatedByHth = serviceRank === RankMethod.HTHTwoWay;
      if (separatedByHth) {
        return result.orderedTeams[0].teamAbbreviation === team1().TeamAbbreviation
          ? [teams[0], teams[1]]
          : [teams[1], teams[0]];
      }
      return team1().GameRecords.AllRecord.Wins() - team1().GameRecords.ConferenceRecord.Wins() >
        team2().GameRecords.AllRecord.Wins() - team2().GameRecords.ConferenceRecord.Wins()
        ? [teams[0], teams[1]]
        : [teams[1], teams[0]];
    } else {
      return team1().GameRecords.AllRecord.Wins() > team2().GameRecords.AllRecord.Wins()
        ? [teams[0], teams[1]]
        : [teams[1], teams[0]];
    }
  }

  getHomeFieldAdvantageForPlayoffs(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const team1 = teams[0];
    const team2 = teams[1];
    if (team1().GameRecords.AllRecord.Wins() === team2().GameRecords.AllRecord.Wins()) {
      const rank1 = team1().RankMethod();
      const rank2 = team2().RankMethod();
      const explanation1 = JSON.stringify(team1().Tiebreakers());
      const explanation2 = JSON.stringify(team2().Tiebreakers());
      const hth = [this.ConferencePickTop([team1, team2] as any)];
      team1().RankMethod(rank1);
      team2().RankMethod(rank2);
      team1().Tiebreakers(JSON.parse(explanation1));
      team2().Tiebreakers(JSON.parse(explanation2));
      if (hth.length === 2) {
        return team1().GameRecords.AllRecord.Wins() - team1().GameRecords.ConferenceRecord.Wins() >
          team2().GameRecords.AllRecord.Wins() - team2().GameRecords.ConferenceRecord.Wins()
          ? [teams[0], teams[1]]
          : [teams[1], teams[0]];
      } else {
        return (hth[0] as any)() === team1() ? [teams[0], teams[1]] : [teams[1], teams[0]];
      }
    } else {
      return team1().GameRecords.AllRecord.Wins() > team2().GameRecords.AllRecord.Wins()
        ? [teams[0], teams[1]]
        : [teams[1], teams[0]];
    }
  }

  generateSeries(
    week: import('../shared/interfaces/IWeek').IWeek,
    team1: KnockoutObservable<ITeam>,
    team2: KnockoutObservable<ITeam>,
    weekNum: number,
    numGames: number,
    gameScheduleNum: number,
    preventReRank?: boolean
  ): number {
    let games: KnockoutObservable<IGame>[] = [];
    let home: KnockoutObservable<ITeam>;
    let away: KnockoutObservable<ITeam>;

    if (gameScheduleNum < 1327) {
      const hth = this.getHomeFieldAdvantageForPlayoffs([team1, team2]);
      home = hth[0];
      away = hth[1];
    } else {
      home = team1;
      away = team2;
    }

    if (numGames === 1) {
      const game1 = ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 1, null, null, weekNum + '1'));
      games.push(game1 as unknown as KnockoutObservable<IGame>);
      this.GameScheduleGameMap[gameScheduleNum + 1] = game1 as unknown as KnockoutObservable<IGame>;
      this.GameArray[gameScheduleNum + 1] = 0;
    } else if (numGames === 3) {
      const game1 = ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 1, null, null, weekNum + '1'));
      const game2 = ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2'));
      const game3 = ko.observable(new NbaGame(away, home, weekNum, gameScheduleNum + 3, null, null, weekNum + '3'));
      games = [game1, game2, game3] as unknown as KnockoutObservable<IGame>[];
      this.GameScheduleGameMap[gameScheduleNum + 1] = game1 as unknown as KnockoutObservable<IGame>;
      this.GameScheduleGameMap[gameScheduleNum + 2] = game2 as unknown as KnockoutObservable<IGame>;
      this.GameScheduleGameMap[gameScheduleNum + 3] = game3 as unknown as KnockoutObservable<IGame>;
      this.GameArray[gameScheduleNum + 1] = 0;
      this.GameArray[gameScheduleNum + 2] = 0;
      this.GameArray[gameScheduleNum + 3] = 0;
    } else {
      // 7-game series — home court hosts games 1,2,5,7
      games = [
        ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 1, null, null, weekNum + '1')),
        ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2')),
        ko.observable(new NbaGame(away, home, weekNum, gameScheduleNum + 3, null, null, weekNum + '3')),
        ko.observable(new NbaGame(away, home, weekNum, gameScheduleNum + 4, null, null, weekNum + '4')),
        ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 5, null, null, weekNum + '5')),
        ko.observable(new NbaGame(away, home, weekNum, gameScheduleNum + 6, null, null, weekNum + '6')),
        ko.observable(new NbaGame(home, away, weekNum, gameScheduleNum + 7, null, null, weekNum + '7')),
      ] as unknown as KnockoutObservable<IGame>[];
      for (const gameObs of games) {
        const gameUnwrapped = (gameObs as KnockoutObservable<NbaGame>)();
        const scheduleNum = gameUnwrapped.gameScheduleNum();
        this.GameScheduleGameMap[scheduleNum] = gameObs;
        this.GameArray[scheduleNum] = 0;
      }
    }

    week.Series.push(new Series(games));
    return gameScheduleNum + numGames;
  }

  /**
   * Build a GameResultsLookup from team schedules (for H2H).
   * Dedupes by gameGuid (or derived key). Only includes games with a selection made.
   */
  private buildGamesLookup(teams: KnockoutObservable<ITeam>[]): ArrayGameResultsLookup {
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${game.gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
        });
      }
    }
    return new ArrayGameResultsLookup(gameResults);
  }

  DivisionPickTop(teamsInADivision: { source: KnockoutObservable<NbaTeam>[] }): KnockoutObservable<NbaTeam> {
    const src = teamsInADivision.source as unknown as KnockoutObservable<ITeam>[];
    const teamEnumerable = Enumerable.From(src.map((p) => p));
    const pct = teamEnumerable
      .Where((p: any) => p().GameRecords.AllRecord.Wins() + p().GameRecords.AllRecord.Losses() > 0)
      .OrderByDescending((p: any) => p().GameRecords.AllRecord.Pct())
      .GroupBy((p: any) => p().GameRecords.AllRecord.Pct())
      .ToArray();
    // Delegate 2-way / 3+ way ranking to extracted service, then overlay
    // DivisionWinner / DivisionWinnerCoinFlip + explanation prose.
    const runService = (group: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam> => {
      const obsMap = buildTeamObsMap(group);
      // For picking a division winner, the teams are ranked within their own
      // division — none of them have been promoted to division winner yet.
      const plainTeams = group.map(o => toNbaTiebreakerTeam(o, false));
      const games = this.buildGamesLookup(group);
      const result = group.length === 2
        ? nbaTwoWayTiebreaker(plainTeams, games)
        : nbaMultiWayTiebreaker(plainTeams, games, true);
      applyTiebreakerResult(result, obsMap);
      const winnerAbbr = result.orderedTeams[0].teamAbbreviation;
      const winnerObs = obsMap.get(winnerAbbr)!;
      const losers = group
        .filter(o => o().TeamAbbreviation !== winnerAbbr)
        .map(o => o().TeamAbbreviation)
        .join(', ');
      if (winnerObs().RankMethod() === RankMethod.CoinFlip) {
        winnerObs().Tiebreakers().Explanations.push('<strong>WARNING:</strong> Division winner was decided by coin flip');
        winnerObs().RankMethod(RankMethod.DivisionWinnerCoinFlip);
        winnerObs().IsDivisionWinner?.(true);
      } else {
        const winnerReason = winnerObs().RankMethodText();
        winnerObs().Tiebreakers().Explanations.push(
          `${winnerAbbr} defeats (${losers}) for division winner based on (${winnerReason})<hr/>`,
        );
        winnerObs().RankMethod(RankMethod.DivisionWinner);
        winnerObs().IsDivisionWinner?.(true);
      }
      return winnerObs;
    };

    if (pct.length && pct[0].source.length) {
      if (pct[0].source.length === 1) {
        pct[0].source[0]().RankMethod(RankMethod.DivisionWinner);
        pct[0].source[0]().IsDivisionWinner?.(true);
        return pct[0].source[0] as any;
      }
      return runService(pct[0].source as KnockoutObservable<ITeam>[]) as any;
    }
    // Fallback: no pct-tied top group — rank entire division via multi-way.
    // Legacy behavior: set DivisionWinner / DivisionWinnerCoinFlip only, no prose.
    const obsMap = buildTeamObsMap(src);
    const plainTeams = src.map(o => toNbaTiebreakerTeam(o, false));
    const games = this.buildGamesLookup(src);
    const result = nbaMultiWayTiebreaker(plainTeams, games, true);
    applyTiebreakerResult(result, obsMap);
    const winnerObs = obsMap.get(result.orderedTeams[0].teamAbbreviation)!;
    if (winnerObs().RankMethod() === RankMethod.CoinFlip) {
      winnerObs().RankMethod(RankMethod.DivisionWinnerCoinFlip);
    } else {
      winnerObs().RankMethod(RankMethod.DivisionWinner);
    }
    winnerObs().IsDivisionWinner?.(true);
    return winnerObs as any;
  }


  ConferenceRanker(teams: KnockoutObservable<ITeam>[], preventRankOverride?: boolean): KnockoutObservable<ITeam>[] {
    if (Enumerable.From(teams).Any((p: any) => p == null)) {
      return [];
    }

    // Build KO observable map
    const teamObsMap = new Map<string, KnockoutObservable<ITeam>>();
    for (const obs of teams) teamObsMap.set(obs().TeamAbbreviation, obs);

    // Convert KO teams to plain tiebreaker objects
    const plainTeams: NbaTiebreakerTeam[] = teams.map(obs => {
      const t = obs();
      const gr = t.GameRecords as any;
      return {
        teamAbbreviation: t.TeamAbbreviation,
        teamId: t.TeamID,
        teamName: t.TeamName,
        division: t.Division,
        conference: t.Conference,
        records: {
          all: { wins: gr.AllRecord.Wins(), losses: gr.AllRecord.Losses(), ties: gr.AllRecord.Ties(),
                 pct: calcPct([gr.AllRecord.Wins(), gr.AllRecord.Losses(), gr.AllRecord.Ties()]),
                 pctNaN: calcPctNaN([gr.AllRecord.Wins(), gr.AllRecord.Losses(), gr.AllRecord.Ties()]) },
          division: { wins: gr.DivisionRecord.Wins(), losses: gr.DivisionRecord.Losses(), ties: gr.DivisionRecord.Ties(),
                      pct: calcPct([gr.DivisionRecord.Wins(), gr.DivisionRecord.Losses(), gr.DivisionRecord.Ties()]),
                      pctNaN: calcPctNaN([gr.DivisionRecord.Wins(), gr.DivisionRecord.Losses(), gr.DivisionRecord.Ties()]) },
          conference: { wins: gr.ConferenceRecord.Wins(), losses: gr.ConferenceRecord.Losses(), ties: gr.ConferenceRecord.Ties(),
                        pct: calcPct([gr.ConferenceRecord.Wins(), gr.ConferenceRecord.Losses(), gr.ConferenceRecord.Ties()]),
                        pctNaN: calcPctNaN([gr.ConferenceRecord.Wins(), gr.ConferenceRecord.Losses(), gr.ConferenceRecord.Ties()]) },
        },
        isDivisionWinner: getNbaDivisionWinnerState(t),
      };
    });

    // Build game results lookup from team schedules (for H2H)
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${game.gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
        });
      }
    }
    const gamesLookup = new ArrayGameResultsLookup(gameResults);

    // Run extracted tiebreaker service (includes same-division gating + restart fix)
    const result = nbaConferenceRanker(plainTeams, gamesLookup);

    // Apply results back to KO observables
    if (!preventRankOverride) {
      for (const [abbr, rankMethod] of result.rankMethods) {
        const obs = teamObsMap.get(abbr);
        if (obs) obs().RankMethod(rankMethod);
      }
    }
    for (const [abbr, explanations] of result.explanations) {
      const obs = teamObsMap.get(abbr);
      if (obs) {
        const tiebreakers = obs().Tiebreakers();
        if (tiebreakers) {
          tiebreakers.Explanations = explanations;
          obs().Tiebreakers.valueHasMutated();
        }
      }
    }

    return result.orderedTeams
      .map(t => teamObsMap.get(t.teamAbbreviation))
      .filter((obs): obs is KnockoutObservable<ITeam> => obs != null);
  }

  ConferencePickTop(teams: KnockoutObservable<ITeam>[], preventRankOverride?: boolean): KnockoutObservable<ITeam> | undefined {
    if (Enumerable.From(teams).Any((p: any) => p == null)) return undefined;
    const ranked = this.ConferenceRanker(teams, preventRankOverride);
    return ranked[0];
  }

  UpdateStats(Position: number, fillWildcard?: boolean): void {
    const self = this;
    self.updateSaveString();
    self.UpdateRankings(Conferences.Western, fillWildcard, Position);
    self.UpdateRankings(Conferences.Eastern, fillWildcard, Position);
    self.pushHistory();
    document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
  }

  pushHistory(): void {
    const self = this;
    const maxWeek = self.MaxWeek();
    BracketGenerator.GenerateBracket(
      self.Weeks[maxWeek + 3],
      self.Weeks[maxWeek + 4],
      self.Weeks[maxWeek + 5],
      self.Weeks[maxWeek + 6]
    );
    super.pushHistory();
  }

  UpdateStatsPostSeason(): void {
    const self = this;
    self.updateSaveString();
    self.pushHistory();
    super.finishComputing();
  }

  // ─── Utility helpers ─────────────────────────────────────────────────────────

  /** CalcGamesBack: standard MLB/NBA games back formula */
  CalcGamesBack(topWins: number, topLosses: number, teamWins: number, teamLosses: number): number {
    return ((topWins - teamWins) + (teamLosses - topLosses)) / 2;
  }

  // All inline tiebreaker helpers (twoWayTiebreaker, multiWayTiebreaker, HtH,
  // CoinFlip, getHTHGames, BestPctNaN/BestDivPctNaN/BestConfPctNaN overrides)
  // have been removed. Tiebreaking is fully delegated to tiebreaker/nba-tiebreaker.ts.
  // Base-class BestPctNaN/BestDivPctNaN/BestConfPctNaN are inherited for primary-sort grouping.

}
